import { Injectable, computed, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

/** The languages this app speaks, in the order the switcher offers them. */
export const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'kn', label: 'ಕನ್ನಡ' }
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]['code'];

const STORAGE_KEY = 'schoolos.language';

/**
 * Which language the app is speaking, and everything that follows from it.
 *
 * Deliberately separate from `AuthService`, for one reason that matters: a
 * parent who reads only Kannada has to be able to switch **before** signing in.
 * The choice therefore lives in its own signal with its own localStorage key,
 * and — unlike the four auth keys — `logout()` does not clear it. Being thrown
 * back into English at the sign-in screen every time you sign out would make
 * the setting useless to the people who need it most.
 *
 * The server is told through `Accept-Language` on every request (see
 * `auth.interceptor.ts`), so a refusal comes back in the language on screen
 * without waiting for the preference to be saved.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly transloco = inject(TranslocoService);

  private readonly _current = signal<LanguageCode>(this.restore());

  /** The language in use. Read this anywhere; it drives the whole UI. */
  readonly current = this._current.asReadonly();

  readonly isKannada = computed(() => this._current() === 'kn');

  constructor() {
    this.apply(this._current());
  }

  /** Switch language. Takes effect immediately — nothing reloads. */
  use(code: LanguageCode): void {
    if (code === this._current()) {
      return;
    }
    this._current.set(code);
    this.apply(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // Non-fatal: the choice simply will not survive a reload.
    }
  }

  /**
   * Reconcile this device with the account, at sign-in. Two directions:
   *
   * * Nothing chosen here yet — take the account's language. A Kannada user on
   *   a new machine should not have to set it again.
   * * Chosen here and different — push it to the account. Somebody who
   *   switched to Kannada *on the sign-in screen* had no account to save it
   *   against at that moment, and their choice has to survive to the next
   *   device rather than living only in this browser.
   *
   * Returns the language to save, or null when there is nothing to do; the
   * caller owns the request, because this service knows nothing about the API.
   */
  reconcile(accountLanguage: string | null | undefined): LanguageCode | null {
    const chosenHere = this.stored();
    if (chosenHere === null) {
      if (accountLanguage && this.isSupported(accountLanguage)) {
        this._current.set(accountLanguage);
        this.apply(accountLanguage);
      }
      return null;
    }
    return chosenHere === accountLanguage ? null : chosenHere;
  }

  private apply(code: LanguageCode): void {
    this.transloco.setActiveLang(code);
    // `lang` drives the font stack and the Kannada typography overrides in
    // styles.css, and tells a screen reader which voice to use.
    document.documentElement.lang = code;
  }

  private isSupported(code: string): code is LanguageCode {
    return LANGUAGES.some(l => l.code === code);
  }

  private stored(): LanguageCode | null {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return value && this.isSupported(value) ? value : null;
    } catch {
      return null;
    }
  }

  private restore(): LanguageCode {
    return this.stored() ?? 'en';
  }
}
